import React, { useState, useEffect } from "react";

import {
	IonContent,
	IonPage,
	IonIcon,
	IonButton,
	IonList,
	IonItem,
	IonLabel,
	useIonLoading,
	IonChip,
	IonListHeader,
	IonToggle,
	useIonToast,
} from "@ionic/react";
import { StoreNames } from "idb";
import { download, cog, trash } from "ionicons/icons";
import { useTranslation } from "react-i18next";

import Header from "../../components/common/Header";
import config from "../../config";
import { dbPromise, FormsDB } from "../../services/idb";
import { estimateStorageQuota, QuotaInfo, deleteOldRecords } from "../../utils/storageUtils";
import LoadJSONModal from "./modals/LoadJSONModal";
import ConfirmDeleteLocal from "./modals/ConfirmDeleteLocal";
import StorageDetails from "./StorageDetails";
import { handleUrlDownload } from "utils/download/handleDownload";

const AdminView: React.FC = () => {
	const [presentLoading, dismissLoading] = useIonLoading();
	const [presentToast] = useIonToast();
	const { t, i18n } = useTranslation();
	const [showStorageDetails, setShowStorageDetails] = useState(false);
	const [showAdvanced, setShowAdvanced] = useState(false);
	const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
	const [openLoadJsonModal, setOpenLoadJsonModal] = useState(false);
	const [quotaEstimation, setQuotaEstimation] = useState<QuotaInfo>({
		quota: undefined,
		usage: undefined,
	});

	useEffect(() => {
		estimateStorageQuota().then(setQuotaEstimation);
	}, []);

	const updateServiceWorker = async () => {
		// Check if we need to login in CloudFlare Access before anything else
		fetch("/service-worker.js").then((res) => {
			console.log("DEBUGGING", res);
			if (res.redirected && res.url.startsWith("https://arup.cloudflareaccess.com")) {
				console.info("Redirecting to cloudflare to get access to the new service worker");
				// This is a bit of a hack:
				// Underscore-prefixed routes are ignored by the service worker (see service-worker.js)
				// this will force the browser to go to the source (cloudflare). Since there's no auth cookie,
				// It'll prompt the user to log in against CloudFlare Access, and get you back to
				// the origin at the "_" route (which is ignored by the router).
				// There's probably a cleaner way to achieve this but this is not too bad
				window.location.replace("/_");
			}
		});
		// Update service worker
		navigator.serviceWorker
			.register("/service-worker.js")
			.then((reg) => {
				reg
					.update()
					.then(() => {
						console.info("Service worker has been successfully updated");
					})
					.catch((err) => console.warn("Error updating worker", err));
			})
			.catch((err) => console.warn("Error registering service worker", err));
	};

	const downloadFile = async (content: string, filename: string) => {
		const blob = new Blob([content], { type: "application/json" });
		const href = await URL.createObjectURL(blob);
		handleUrlDownload(href, filename);
	};

	const dumpLocalData = async () => {
		presentLoading({
			message: i18n.format(t("saving_data"), "capitalize"),
		});
		const stores: StoreNames<FormsDB>[] = ["projects", "assets", "records", "images", "forms"];
		const timestamp = new Date().getTime();
		const data: Record<string, unknown> = {};
		for (const key of stores) {
			const dump = await (await dbPromise).getAll(key);
			data[key] = dump;
		}
		const fileName = `${timestamp}-dhub-forms.json`;
		const imageStore = await (await dbPromise).getAll("images");
		for (const image of imageStore) {
			const file = image.file;
			if (file) {
				const blob = new Blob([file], { type: file.type });
				const href = await URL.createObjectURL(blob);
				// TODO: Chrome doesnt like downloading multiple files at once, could zip it.
				handleUrlDownload(href, image.filepath);
			}
		}
		await downloadFile(JSON.stringify(data), fileName);
		dismissLoading();
	};

	return (
		<IonPage>
			<Header title="Admin Panel" backUrl="/" />
			<IonContent forceOverscroll={false}>
				<IonList>
					<IonListHeader lines="full">
						<IonLabel>{i18n.format(t("about_this"), "capitalize")}</IonLabel>
					</IonListHeader>
					<IonItem lines="none">
						<IonLabel>{i18n.format(t("origin"), "capitalize")}</IonLabel>
						<IonChip>{window.location.origin}</IonChip>
					</IonItem>
					<IonItem lines="none">
						<IonLabel>{i18n.format("API", "capitalize")}</IonLabel>
						<IonChip>{config.API}</IonChip>
					</IonItem>
					<IonItem lines="none">
						<IonLabel>{i18n.format(t("version"), "capitalize")}</IonLabel>
						<IonChip>{config.RELEASE}</IonChip>
					</IonItem>
					<IonItem lines="none">
						<IonLabel>{i18n.format(t("storage_usage"), "capitalizeEveryWord")}</IonLabel>
						<IonChip>{quotaEstimation.usage ? `${(quotaEstimation.usage / 1e6).toFixed(0)}Mb` : undefined}</IonChip>
					</IonItem>
					<IonItem>
						<IonToggle onIonChange={() => setShowStorageDetails((it) => !it)}>
							{i18n.format(t("showStorageSummary"), "capitalize")}
						</IonToggle>
					</IonItem>
					<IonItem>
						<IonToggle onIonChange={() => setShowAdvanced((it) => !it)}>
							{i18n.format(t("showAdvancedActions"), "capitalize")}
						</IonToggle>
					</IonItem>
					<IonButton onClick={updateServiceWorker} expand="block" style={{ margin: "1rem" }}>
						{i18n.format(t("trigger_update"), "capitalize")}
						<IonIcon slot="end" src={cog} />
					</IonButton>
				</IonList>
				{showStorageDetails && <StorageDetails />}
				{showAdvanced && (
					<IonList>
						<IonListHeader lines="full">
							<IonLabel>{i18n.format(t("advanced_options"), "capitalize")}</IonLabel>
						</IonListHeader>
						<IonButton onClick={() => setOpenLoadJsonModal(true)} expand="block" style={{ margin: "1rem" }}>
							{i18n.format(t("load_local_json"), "capitalize")}
							<IonIcon slot="end" src={download} style={{ transform: "rotate(180deg)" }} />
						</IonButton>
						<IonButton onClick={dumpLocalData} expand="block" style={{ margin: "1rem" }}>
							{i18n.format(t("download_local"), "capitalize")}
							<IonIcon slot="end" src={download} />
						</IonButton>
						<IonButton
							expand="block"
							onClick={() =>
								deleteOldRecords().then((msg) =>
									presentToast({ message: msg, duration: 3000, position: "top", buttons: [{ text: "Ok" }] }),
								)
							}
							style={{ margin: "1rem" }}
						>
							{i18n.format(t("delete_unused"), "capitalize")}
							<IonIcon slot="end" src={trash} />
						</IonButton>
						<IonButton expand="block" onClick={() => setIsOpenDeleteModal(true)} style={{ margin: "1rem" }}>
							{i18n.format(t("delete_local"), "capitalize")}
							<IonIcon slot="end" src={trash} />
						</IonButton>
					</IonList>
				)}
			</IonContent>
			{/* Sheet Modal */}
			<ConfirmDeleteLocal isOpen={isOpenDeleteModal} setIsOpen={setIsOpenDeleteModal} />
			<LoadJSONModal isOpen={openLoadJsonModal} setIsOpen={setOpenLoadJsonModal} />
		</IonPage>
	);
};

export default AdminView;
